// elements/product-media-v2.js
import StencilHTMLElement from "./stencil-element";

class ProductMediaV2 extends StencilHTMLElement {
  name = "ProductMediaV2";

  swiperThumbs;
  swiperMedia;
  pswpItems = [];
  firstVideoIndex = null;

  constructor() {
    super();
  }

  connectedCallback() {
    // Parse embedded config JSON
    const cfgEl = this.querySelector('script[type="application/json"].media-config');
    const pswpEl = this.querySelector('script[type="application/json"].pswp-data');

    const cfg = cfgEl?.textContent?.trim() ? JSON.parse(cfgEl.textContent) : {};
    this.pswpItems = pswpEl?.textContent?.trim() ? JSON.parse(pswpEl.textContent) : [];

    const setToActiveVariant = String(cfg.setToActiveVariant || "") === "true";
    const activeVariantId = cfg.activeVariantId ? String(cfg.activeVariantId) : null;
    const has3D = String(cfg.has3DModel || "") === "true";

    // Find initial slide (optionally by variant)
    let initialSlide = 0;
    if (setToActiveVariant && activeVariantId) {
      const match = this.querySelector(`.swiper-media .swiper-slide[data-variants*='${activeVariantId}']`);
      if (match && match.dataset && match.dataset.index) {
        const idx = parseInt(match.dataset.index, 10);
        if (!Number.isNaN(idx)) {
          initialSlide = idx;
        }
      }
    }

    // Thumb swiper
    const thumbsEl = this.querySelector(".swiper-thumbs");
    if (thumbsEl) {
      this.swiperThumbs = new Swiper(thumbsEl, {
        spaceBetween: 8,
        freeMode: true,
        watchSlidesProgress: true,
        direction: "vertical",
        slidesPerView: "auto",
        breakpoints: {
          992: {
            direction: "horizontal",
            slidesPerView: 16
          }
        }
      });
    }

    // Main swiper
    const mediaEl = this.querySelector(".swiper-media");
    if (mediaEl) {
      this.swiperMedia = new Swiper(mediaEl, {
        // If a 3D model is present, match your current behaviour
        allowTouchMove: has3D ? false : true,
        loop: true,
        direction: "horizontal",
        slidesPerView: "auto",
        centeredSlides: false,
        initialSlide: initialSlide,
        spaceBetween: 0,

        navigation: {
          nextEl: this.querySelector(".swiper-button-next"),
          prevEl: this.querySelector(".swiper-button-prev")
        },

        breakpoints: {
          768: {
            spaceBetween: 8
          }
        },

        thumbs: this.swiperThumbs
          ? { swiper: this.swiperThumbs }
          : undefined,

        on: {
          init: () => {
            if (typeof window.mp_lazyload_setup === "function") {
              window.mp_lazyload_setup();
            }
            this.#maybeAutoplayActiveVideo();
          },
          slideChangeTransitionEnd: (sw) => {
            this.#maybeAutoplayActiveVideo();
          }
        }
      });
    }

    // Photoswipe handlers
    this.#wirePhotoSwipeTriggers();
    this.#wireOpenVideoButton();
  }

  disconnectedCallback() {
    if (this.swiperMedia) {
      this.swiperMedia.destroy(true, true);
      this.swiperMedia = undefined;
    }
    if (this.swiperThumbs) {
      this.swiperThumbs.destroy(true, true);
      this.swiperThumbs = undefined;
    }
  }

  #wirePhotoSwipeTriggers() {
    if (!Array.isArray(this.pswpItems) || this.pswpItems.length === 0) {
      return;
    }

    const slides = this.querySelectorAll(".swiper-media .swiper-slide");
    slides.forEach((slide) => {
      slide.addEventListener("click", (e) => {
        e.preventDefault();
        const idxAttr = slide.getAttribute("data-swiper-slide-index") || slide.getAttribute("data-index");
        const index = Math.max(0, parseInt(idxAttr || "0", 10));
        this.#openPhotoSwipe(index);
      });
    });
  }

  #wireOpenVideoButton() {
    const btn = this.querySelector("#open-video");
    if (!btn) {
      return;
    }

    // If not precomputed, derive first video index from pswp items
    if (this.firstVideoIndex === null && Array.isArray(this.pswpItems)) {
      const i = this.pswpItems.findIndex((it) => !!it.html); // videos in your data use `html`
      this.firstVideoIndex = i >= 0 ? i : null;
    }

    if (this.firstVideoIndex !== null) {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        this.#openPhotoSwipe(this.firstVideoIndex);
      });
    } else {
      // Hide if no video
      btn.style.display = "none";
    }
  }

  #openPhotoSwipe(index) {
    if (typeof window.PhotoSwipe !== "function") {
      return;
    }

    const options = { dataSource: this.pswpItems, index };
    const pswp = new PhotoSwipe(options);

    // Autoplay video when slide with video is visible
    const playCurrentVideo = () => {
      const vid = this.querySelector(
        `.product-media-video video`
      );
      if (vid) {
        try {
          vid.play();
        } catch {}
      }
    };

    pswp.on("change", playCurrentVideo);
    pswp.on("loadComplete", playCurrentVideo);
    pswp.init();
  }

  #maybeAutoplayActiveVideo() {
    const active = this.querySelector(".swiper-media .swiper-slide-active");
    if (!active) {
      return;
    }
    const vid = active.querySelector("video");
    if (vid) {
      try {
        vid.play();
      } catch {}
    }
  }
}

export default ProductMediaV2;